import WidgetModel from "@/models/WidgetModel";
import PatientService from "@/components/services/PatientService";
import ColorPicker from "@/components/ColorPicker";
import to from "@/lib/to";
import {EventBus, EVENTS} from "@/lib/EventBus";

/**
 * Emit widget specific actions such as reload
 * @typedef {Object} WidgetAction
 * @property {String|Function} title - String title of the action or function returning string
 * @property {String} icon - Icon for the action
 * @property {Function} isVisible - Function called on render to decide if action is visible
 * @property {Boolean} isClickable - Whether action item is clickable. FALSE used for displaying information
 * @property {Function} handler - Action handler
 * @property {'is-warning'|'is-danger'|'is-success'} type - changes color of icon and text
 */

export default {
  props: {
    widgetModel: {
      type: WidgetModel,
      required: true
    }
  },
  widget: {
    serviceMethod: null,
    title: 'DEFAULT TITLE',
    actions: [],
    guard: null,
    errorHandler: null
  },
  methods: {
    performRequest() {
      if (this.$options.widget.serviceMethod) {
        this.promise = this.service.withoutErrorHandling()[this.$options.widget.serviceMethod]({...this.widgetModel.params, ...{ patientId: this.widgetModel.patientId, monitorId: this.widgetModel.monitorId }}).then(response => {
          if (this.$options.afterDataLoaded) {
            this.$options.afterDataLoaded.call(this, response)
            this.httpResponse = response
          }
          return response
        })
      } else {
        if (this.$options.afterDataLoaded) {
          this.$options.afterDataLoaded.call(this)
        }
      }
    },
     /**
     * @param {WidgetAction[]} actions
     */
    emitActions(actions = []) {
      let allActions = this._getDefaultActions()
      allActions.push(...actions)
      let newActions = []
      for (const action of allActions) {
        const init = action.init
        const isVisible = action.isVisible
        action._isVisible = true
        if (init) {
          init.call(this, action)
        }
        if (isVisible) {
          action._isVisible = isVisible.call(this, action)
        }
        let title = ''
        if (typeof action.title == 'string') {
          title = action.title
        } else if (typeof action.title == 'function') {
          title = action.title.call(this, action)
        }

        let icon = ''
        if (typeof action.icon == 'function') {
          icon = action.icon.call(this, action)
        } else {
          icon = action.icon
        }

        let componentAttributes = {}
        if (typeof action.componentAttributes === 'function') {
          componentAttributes = action.componentAttributes.call(this)
        }

        if (action.hasTopSeparator) {
          newActions.push({
            separator: true,
            _isVisible: action._isVisible
          })
        }

        newActions.push({
          ...action,
          handler: action.handler ? action.handler.bind(this) : null,
          title,
          icon,
          componentAttributes
        })

        if (action.hasBottomSeparator)  {
          newActions.push({
            separator: true,
            _isVisible: action._isVisible
          })
        }
      }
      this.$emit('settings-updated', newActions)
    },
    emitTitle(title) {
      this.$emit('title-updated', title)
      this.title = title
    },
    emitSubtitle(subtitle) {
      this._emitSubtitle(subtitle)
    },
    emitTitleTooltip(tooltip) {
      this.$emit('title-tooltip-updated', tooltip)
    },
    _emitSubtitle(subtitle) {
      this.$emit('subtitle-updated', subtitle)
      this.subtitle = subtitle
    },
    emitWidgetColor(color, isAnimated = true) {
      this.$emit('color-updated', color, isAnimated)
    },
    emitExpanded(isExpanded) {
      this.$emit('size-changed', isExpanded)
      this.isExpandedMode = isExpanded
    },
    setErrorTitles() {
      this.emitTitle('Error')
      this._emitSubtitle('  ')
    },
    emitHideToolbar() {
      this.$emit('hide-toolbar', true)
    },
    emitShowToolbar() {
      this.$emit('hide-toolbar', false)
    },
    setErrorMode() {
      this.hasError = true
      this.emitHideToolbar()
      this.setErrorTitles()
    },
    async _fetchMonitor() {
      let req = this.service.withoutErrorHandling().getMonitors({ monitorId: this.widgetModel.monitorId })
      const [err, response] = await to(req)
      if (err) {
        this.setErrorMode()
        if (err.response?.data?.type == 'ActiveRecord::RecordNotFound') {
          this.errorMessage = "The monitor could not be found. You may not have access to this patient."
        }
        this.$options.widget.errorHandler?.call(this, err)
      } else {
        this.widgetModel.monitor = response.data
      }
      return req
    },
    getSubtitleAndRoomStatus() {
      if (this.widgetModel.monitor) {
        let title = `${this.$helpers.mask('patient_name', this.widgetModel.monitor.patient.display_name)}`
        title += ` (${this.widgetModel.monitor.patient.gender[0].toUpperCase()})`
        this.emitTitle(title)
      }
    },
    setIsHistoricalBadge() {
      this.widgetModel.settings.isHistorical = this.widgetModel.monitor.monitor_ended_at != null
      if (this.widgetModel.settings.isHistorical) {
        this.widgetModel.settings.badge = {
          enabled: true,
          title: 'historical data',
          type: '',
          tooltipText: `Monitoring Stopped: ${this.$helpers.formatDate(this.widgetModel.monitor.monitor_ended_at)}`
        }
      }
    },
    setPausedBadge() {
      if (this.widgetModel.monitor.status == 'paused') {
        this.widgetModel.settings.badge = {
          enabled: true,
          title: 'monitoring paused',
          type: 'is-warning',
          tooltipText: `Monitoring Paused: ${this.$helpers.formatDate(this.widgetModel.monitor.updated_at)}`
        }
      }
    },
    setUpdateAvailableBadge() {
      if (this.widgetModel.monitor.video_device?.state?.pending_software_update && !this.deviceRebootCommandSent) {
        this.widgetModel.settings.badge = {
          enabled: true,
          title: 'Install Camera Update?',
          type: 'is-success',
          tooltipText: `At a convenient time, please reboot this device to install the latest update.  Video will be unavailable for 2-3 minutes during the update.`
        }
      }
    },
    setRoomMismatchBadge() {
      if (this.widgetModel.monitor.status != 'active') {
        return
      }

      const [{ room: monitorRoom }, { room: patientRoom }] = [this.widgetModel.monitor, this.widgetModel.monitor.patient]
      if (monitorRoom.id != patientRoom?.id) {
        this.widgetModel.settings.badge = {
          enabled: true,
          title: 'Room Mismatch',
          type: 'is-warning',
          tooltipText: `Monitoring Room (${monitorRoom.name}) does not match Assigned Room (${patientRoom?.name || 'None'})`
        }
        this.$store.commit('CentralMonitoringStore/pushRoomMismatchMonitors', this.widgetModel.monitorId)
        EventBus.$emit(EVENTS.ROOM_MISMATCH, { monitor: this.widgetModel.monitor })
      } else {
        this.$store.commit('CentralMonitoringStore/deleteRoomMismatchMonitors', this.widgetModel.monitorId)
      }
    },
    setPendingAlarmsBadge() {
      if (this.widgetModel.monitor.status != 'active') return

      const pendingAlarms = this.widgetModel.monitor.stat_alarms.filter(alarm =>
        alarm.alarm_type == 'alarm' && !alarm.description && !alarm.stat_alarm_category_id
      ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))

      if (pendingAlarms.length === 0) {
        if (this.widgetModel.settings.badge.title == 'Alarm Activated') {
          this.widgetModel.settings.badge.enabled = false
        }
        this.currentActiveAlarm = null
        this.clearAlarmTimer()
      } else {
        this.widgetModel.settings.badge = {
          enabled: true,
          title: 'Alarm Activated',
          type: 'is-error',
          tooltipText: this.$helpers.formatDate(pendingAlarms[0].updated_at, 'llll')
        }

        if (this.widgetModel.monitor?.stat_alarms?.length) {
          const newestAlarm = this.widgetModel.monitor.stat_alarms.reduce((newest, current) =>
            new Date(current.created_at) > new Date(newest.created_at) ? current : newest
          )
          this.setCurrentActiveAlarm(newestAlarm)
        }
      }
    },

    setCurrentActiveAlarm(alarm) {
      const division = this.$store.state.currentUser.divisions.find(d => d.id == this.widgetModel.monitor.division_id)
      if (!division?.video_device_alarm_name || !division?.video_device_alarm_duration) {
        this.currentActiveAlarm = null
        return
      }

      const alarmEnd = new Date(alarm.created_at).getTime() + (division.video_device_alarm_duration * 1000)
      const now = new Date().getTime()

      if (now >= alarmEnd) {
        this.currentActiveAlarm = null
        return
      }

      this.currentActiveAlarm = alarm
      this.setupAlarmTimer(alarmEnd)
    },
    setupAlarmTimer(alarmEnd) {
      this.clearAlarmTimer()

      const now = new Date().getTime()
      const timeUntilExpiry = alarmEnd - now

      this.alarmTimer = setTimeout(() => {
        this.currentActiveAlarm = null
      }, timeUntilExpiry)
    },
    clearAlarmTimer() {
      if (this.alarmTimer) {
        clearTimeout(this.alarmTimer)
        this.alarmTimer = null
      }
    },
    async stopAlarmSound() {
      if (!this.widgetModel?.monitor?.video_device?.id) return

      const [err] = await to(this.$http.post(`/video_devices/${this.widgetModel.monitor.video_device.id}/stop_alarm_sound`))
      if (!err) {
        this.currentActiveAlarm = null
        this.clearAlarmTimer()
      }
    },
    setupBadges() {
      this.widgetModel.settings.badge = { enabled: false }
      this.setUpdateAvailableBadge()
      this.setIsHistoricalBadge()
      this.setPausedBadge()
      this.setRoomMismatchBadge()
      this.setPendingAlarmsBadge()
    },
    updateWidget(data) {
      const { settings: newSettings, params: newParams } = data
      const mergedSettings = {...this.widgetModel.settings, ...newSettings}
      delete mergedSettings.badge
      this.service.updateWidget(this.widgetModel.id, {
        settings: mergedSettings,
        params: {...this.widgetModel.params, ...newParams}
      })
    },
    _getDefaultActions() {
      let actions = []
      if(this.widgetModel.name != 'live-video') {
        actions.push({
          title: 'Reload',
          icon: 'mdi mdi-reload',
          hasBottomSeparator: true,
          handler: () => {
            this.performRequest()
          }
        })
      }
      if (this.$store.state.currentUser.enabled_flags.includes('block_and_chart_testing')) {
        actions.push({
          title: 'Set Colors',
          icon: 'mdi mdi-format-color-highlight',
          handler: () => {
            this.$buefy.modal.open({
              component: ColorPicker,
              hasModalCard: true,
              customClass: 'dialog',
              props: {
                color: this.widgetModel.settings.borderColor || ''
              },
              events: {
                save: (value) => {
                  if (!this.widgetModel.isGuest) {
                    this.updateWidget({
                      settings: {
                        borderColor: value
                      }
                    });
                  }
                  this.widgetModel.settings.borderColor = value
                  this.emitWidgetColor(value)
                }
              }
            });
          }
        })
      }
      // actions.push({
      //   title: 'Adjust Size',
      //   icon: 'mdi mdi-resize',
      //   handler: () => {
      //     this.widgetModel.settings.isExpanded = !this.widgetModel.settings.isExpanded
      //     this.emitExpanded(this.widgetModel.settings.isExpanded)
      //     if (!this.widgetModel.isGuest) {
      //       this.updateWidget({
      //         settings: {
      //           isExpanded: this.widgetModel.settings.isExpanded
      //         }
      //       })
      //     }
      //     this.emitActions(this.$options.widget.actions)
      //   }
      // })
      return actions
    },
    loadExistingMonitorColor() {
      if (Object.prototype.hasOwnProperty.call(this.pusherColorMap, this.widgetModel.monitor.color)) {
        this.$nextTick(() => {
          this.emitWidgetColor(this.pusherColorMap[this.widgetModel.monitor.color])
        })
      }
    },
    async reloadWidget() {
      if (this.widgetModel.settings.retrievePatientInfo) {
        const [err, ] = await to(this._fetchMonitor())
        if (!err) {
          this.getSubtitleAndRoomStatus()
          this.setupBadges()
        }
      }
      if (!this.hasError) {
        this.performRequest()
        this.emitActions(this.$options.widget.actions)
        this._emitSubtitle(this.$options.widget.title)
      }
      this.emitExpanded(this.widgetModel.settings.isExpanded)
    },
    monitorUpdatedEventHandler(data) {
      if (Array.isArray(data)) {
        data.forEach(x => {
          if (x.monitor_id == this.widgetModel.monitorId) {
            if (Object.prototype.hasOwnProperty.call(this.pusherColorMap, x.color)) {
              this.emitWidgetColor(this.pusherColorMap[x.color])
            }
          }
        })
      }
    },
    roomMismatchEventHandler({ monitor_id }) {
      if (monitor_id == this.widgetModel.monitorId) {
        this.reloadWidget()
      }
    },
    async reloadWidgetEventHandler(monitorId) {
      if (this.widgetModel.monitor && this.widgetModel.monitor.id == monitorId) {
        await this.reloadWidget()
      }
    }
  },
  beforeCreate() {
    this.$options.widget.actions = Array.isArray(this.$options.widget.actions) ? this.$options.widget.actions : []
  },
  created() {
    const channelName = `private-encrypted-ld-${import.meta.env.VITE_ENVIRONMENT_NAME}-hospital-${this.$store.state.currentUser.hospital_id}`
    this.pusherChannel = this.$pusher.subscribe(channelName)
    this.pusherChannel.bind('monitor-updated', this.monitorUpdatedEventHandler)
    this.pusherChannel.bind('patient-monitor-room-mismatch', this.roomMismatchEventHandler)
  },
  beforeUnmount() {
    this.pusherChannel?.unbind('monitor-updated', this.monitorUpdatedEventHandler)
    this.pusherChannel?.unbind('patient-monitor-room-mismatch', this.roomMismatchEventHandler)
    EventBus.$off(EVENTS.RELOAD_WIDGET_MONITOR_DATA, this.reloadWidgetEventHandler)
    if (this.alarmTimer) {
      clearTimeout(this.alarmTimer)
    }
  },
  async beforeMount() {
    if (typeof this.$options.widget.guard === 'function') {
      if (this.$options.widget.guard.call(this)) {
        this.hasError = false
        this.reloadWidget()
      } else {
        this.hasError = true
      }
    } else {
      this.reloadWidget()
    }
  },
  mounted() {
    if (this.widgetModel.settings.retrievePatientInfo) {
      const unwatch = this.$watch('widgetModel.monitor', newVal => {
        if (newVal) {
          const { borderColor } = this.widgetModel.settings
          if (borderColor) {
            this.emitWidgetColor(borderColor)
          } else {
            this.loadExistingMonitorColor()
          }
          setTimeout(() => unwatch())
        }
      }, { immediate: true })
    }
    EventBus.$on(EVENTS.RELOAD_WIDGET_MONITOR_DATA, this.reloadWidgetEventHandler)
  },
  watch: {
    async widgetModel(newModel, oldModel) {
      /**
       * sometimes widget can be build only with monitorId, in that case widget mixin
       * will automatically fetch whole monitor data by provided ID.
       * In some cases when widget instance stays same and widgetModel changes,
       * new model might still not have monitor data, in that case, instead of reloading
       * we re-use old data
       */
      if (!newModel.monitor && !!oldModel.monitor) {
        this.widgetModel.monitor = oldModel.monitor
      } else {
        if (newModel.monitor) {
          this.widgetModel.monitor = newModel.monitor
        } else {
          await this._fetchMonitor()
        }
      }
      this.widgetModel.monitor = newModel.monitor
      this.getSubtitleAndRoomStatus()
      this.setupBadges()
      this.emitActions(this.$options.widget.actions)
    }
  },
  data() {
    return {
      monitor: null,
      subtitle: '',
      title: '',
      service: PatientService(this.$http),
      promise: new Promise(() => {}),
      hasError: false,
      errorMessage: "Something went wrong. Please reload page.",
      isExpandedMode: false,
      httpResponse: null,
      pusherColorMap: { red: '#D07E7E', yellow: '#EEC87E', none: '' },
      pusherChannel: null,
      deviceRebootCommandSent: false,
      alarmTimer: null,
      currentActiveAlarm: null
    }
  }
}
